import { useEffect } from 'react';
import { create } from 'zustand';

// A dirty drawer form left behind by navigating away. "Go back" in the
// unsaved-changes toast flips `restoring`; the page then reopens the drawer
// (lazy useState init via getRestoringDraft) and the drawer re-applies values.
export interface DrawerDraft {
  key: string;
  entity: unknown;
  values: unknown;
  returnTo: string;
  restoring: boolean;
}

interface DrawerDraftState {
  draft: DrawerDraft | null;
  stash: (draft: Omit<DrawerDraft, 'restoring'>) => void;
  requestRestore: () => void;
  clear: () => void;
}

export const useDrawerDraftStore = create<DrawerDraftState>((set) => ({
  draft: null,
  stash: (draft) => set({ draft: { ...draft, restoring: false } }),
  requestRestore: () =>
    set((state) => (state.draft ? { draft: { ...state.draft, restoring: true } } : state)),
  clear: () => set({ draft: null }),
}));

export function getRestoringDraft<TEntity = unknown>(key: string) {
  const { draft } = useDrawerDraftStore.getState();
  if (!draft?.restoring || draft.key !== key) return null;
  return { entity: draft.entity as TEntity };
}

export interface DrawerDraftConfig {
  key: string;
  entity?: unknown;
  getValues: () => unknown;
}

export function useDrawerDraft<TValues>({
  key,
  entity,
  open,
  ready = true,
  getValues,
  restore,
}: {
  key: string;
  entity?: unknown;
  open: boolean;
  ready?: boolean;
  getValues: () => TValues;
  restore: (values: TValues) => void;
}): DrawerDraftConfig {
  const pending = useDrawerDraftStore((s) =>
    s.draft?.restoring && s.draft.key === key ? s.draft : null,
  );
  const clear = useDrawerDraftStore((s) => s.clear);

  useEffect(() => {
    if (!pending) return;
    // Closed without the draft ever landing (e.g. entity gone) — drop it so
    // the page doesn't reopen the drawer on every visit
    if (!open) {
      clear();
      return;
    }
    if (!ready) return;
    restore(pending.values as TValues);
    clear();
  }, [pending, open, ready, restore, clear]);

  return { key, entity, getValues };
}
